/**
 * LiveDashboard — Pad táctil "fat-finger" para la pestaña Peticiones en modo Live.
 *
 * Grid 4x2 (landscape) / 2x4 (portrait). 8 botones gigantes. Cada botón mapea
 * a una petición (título + nota como etiqueta + icono de origen). Al pulsar:
 *   - MP3 caché  → reproduce el elemento <audio> interno.
 *   - YouTube    → carga iframe con autoplay=1.
 *   - Sólo Spotify → dispara openExternalLink al deep link.
 *
 * Regla de un-solo-activo: al pulsar botón B con A sonando, fade-out ~800ms del A
 * y arranque limpio del B.
 *
 * Sin fader (decisión producto: iOS Safari no permite control programático de
 * volumen; el DJ usa el volumen físico del iPhone/iPad).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Music, Youtube, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { openExternalLink } from '../utils/openExternalLink';
import { haptic } from '../utils/haptic';
import { getAudioFromCache } from '../utils/audioCache';
import { resolveSpotifyUrl } from '../utils/spotifyUrl';
import { guessBucketFromPeticion, getDisplayLabel } from '../utils/momentoBuckets';
import { LiveMomentoSwitcher } from './LiveMomentoSwitcher';

const PAGE_SIZE = 8;
// Fade-out corto: 250ms preserva el "user gesture" context de iOS y evita
// que el navegador rechace el `audio.play()` del nuevo track por perder el
// contexto de gesto (iOS es estricto con esto — >800ms puede caducar).
const FADE_OUT_MS = 250;
// Umbral a partir del cual una petición se considera "reproducida" para el
// sombreado visual en el pad. NO afecta al flag `reproducida` del backend
// (que se sigue gestionando manualmente desde la lista drag-drop).
const PLAYED_THRESHOLD = 0.3;
const playedStorageKey = (playlistId) => `livepad_played_${playlistId || 'unknown'}`;

// Bordes vibrantes por slot (rotación cíclica de 4 colores). Alto contraste sobre negro.
const SLOT_ACCENTS = [
  { border: '#22d3ee', glow: 'rgba(34,211,238,0.55)' },  // cyan
  { border: '#e879f9', glow: 'rgba(232,121,249,0.55)' }, // magenta
  { border: '#fbbf24', glow: 'rgba(251,191,36,0.55)' },  // amber
  { border: '#a3e635', glow: 'rgba(163,230,53,0.55)' },  // lime
];

const getSourceKind = (p) => {
  if (p?.audio_file_id) return 'mp3';
  if (p?.youtube_url) return 'youtube';
  // Feb-2026: si no hay MP3/YouTube, cualquier petición con datos de canción
  // (spotify_url/uri/id o título+artista) se abre en Spotify como fallback.
  if (resolveSpotifyUrl(p)) return 'spotify';
  return 'none';
};

const SourceIcon = ({ kind }) => {
  if (kind === 'mp3') return <Music className="w-5 h-5" />;
  if (kind === 'youtube') return <Youtube className="w-5 h-5" />;
  if (kind === 'spotify') return <ExternalLink className="w-5 h-5" />;
  return null;
};

// Extrae ID de YouTube desde URL para usar en embed.
const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
};

const LiveDashboard = ({
  peticiones = [],
  playlistId,
  token,
  activeBucket = 'baile',
  onActiveBucketChange,
  momentoOverrides = {},
}) => {
  const [page, setPage] = useState(0);
  const [activeId, setActiveId] = useState(null); // id de la petición actualmente sonando
  const [loadingId, setLoadingId] = useState(null);
  // Progreso de la petición activa (0-1). Ahora también para YouTube vía IFrame API.
  const [progress, setProgress] = useState(0);
  // Set de IDs de peticiones que ya alcanzaron el umbral PLAYED_THRESHOLD.
  // Se hidrata desde localStorage por playlistId → persiste sobrevive a
  // cerrar/reabrir la PWA dentro del mismo evento.
  const [playedIds, setPlayedIds] = useState(() => {
    if (typeof window === 'undefined' || !playlistId) return new Set();
    try {
      const raw = window.localStorage.getItem(playedStorageKey(playlistId));
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch (e) {
      return new Set();
    }
  });

  // Refs de reproductores
  const audioRef = useRef(null); // <audio> HTML5 para MP3
  const youtubeIframeRef = useRef(null); // iframe YT (destino DOM)
  const ytPlayerRef = useRef(null); // instancia YT.Player (IFrame API)
  const ytProgressIntervalRef = useRef(null);
  const currentPlayerRef = useRef(null); // 'mp3' | 'youtube' | null → cuál está activo
  const fadeIntervalRef = useRef(null);
  // Guard para evitar dobles taps rápidos que se pisan.
  const switchingRef = useRef(false);
  // CUE de arranque pendiente para el siguiente MP3 (se aplica al `loadedmetadata`
  // si duration aún no estaba disponible al llamar play()).
  const pendingCueSeekRef = useRef(null);
  // Pending YouTube video para cuando la API aún no está lista al pulsar
  const pendingYouTubeRef = useRef(null); // { videoId, startSeconds }

  // Re-hidratación al cambiar de playlist (si el componente se reutiliza entre eventos).
  useEffect(() => {
    if (!playlistId) return;
    try {
      const raw = window.localStorage.getItem(playedStorageKey(playlistId));
      const arr = raw ? JSON.parse(raw) : [];
      setPlayedIds(new Set(Array.isArray(arr) ? arr : []));
    } catch (e) {
      setPlayedIds(new Set());
    }
  }, [playlistId]);

  // Marca una petición como "reproducida (visual)" cuando alcanza el umbral.
  // Idempotente + persiste inmediatamente a localStorage.
  const markPlayed = useCallback(
    (peticionId) => {
      if (!peticionId) return;
      setPlayedIds((prev) => {
        if (prev.has(peticionId)) return prev;
        const next = new Set(prev);
        next.add(peticionId);
        try {
          if (playlistId) {
            window.localStorage.setItem(
              playedStorageKey(playlistId),
              JSON.stringify(Array.from(next))
            );
          }
        } catch (e) { /* silencioso (quota, iOS Safari private mode) */ }
        return next;
      });
    },
    [playlistId]
  );
  useEffect(() => {
    const ensureYTPlayer = () => {
      if (ytPlayerRef.current || !youtubeIframeRef.current) return;
      if (!window.YT || !window.YT.Player) return;
      try {
        ytPlayerRef.current = new window.YT.Player(youtubeIframeRef.current, {
          events: {
            onReady: () => {
              // Si el usuario ya tocó un botón YT antes de estar listo, arrancarlo ahora
              const pending = pendingYouTubeRef.current;
              if (pending && ytPlayerRef.current?.loadVideoById) {
                ytPlayerRef.current.loadVideoById(pending);
                pendingYouTubeRef.current = null;
              }
            },
            onStateChange: (ev) => {
              // 0 = ENDED. Limpia estado.
              if (ev.data === 0) {
                setActiveId(null);
                setProgress(0);
                currentPlayerRef.current = null;
                if (ytProgressIntervalRef.current) {
                  clearInterval(ytProgressIntervalRef.current);
                  ytProgressIntervalRef.current = null;
                }
              }
            },
          },
        });
      } catch (e) {
        console.warn('[LiveDashboard] YT.Player init failed:', e);
      }
    };
    // Si la API ya está cargada por otro componente, crea el player directo.
    if (window.YT && window.YT.Player) {
      ensureYTPlayer();
      return;
    }
    // Instala el callback global antes de inyectar el script.
    const prevCb = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prevCb === 'function') prevCb();
      ensureYTPlayer();
    };
    // Inyecta el script una vez
    if (!document.querySelector('script[data-yt-iframe-api]')) {
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      s.async = true;
      s.dataset.ytIframeApi = '1';
      document.head.appendChild(s);
    }
    return () => {
      if (ytProgressIntervalRef.current) {
        clearInterval(ytProgressIntervalRef.current);
        ytProgressIntervalRef.current = null;
      }
    };
  }, []);

  // Filtro: peticiones válidas para el pad (no reproducidas, no rechazadas).
  // NOTA: `hidden_from_client` es un flag "sorpresa" (cliente no la ve) pero
  // el DJ SÍ debe tenerla accesible en el pad — por eso NO se filtra aquí.
  const activePeticiones = useMemo(
    () =>
      (peticiones || []).filter(
        (p) =>
          !p.reproducida &&
          !p.rechazada &&
          getSourceKind(p) !== 'none' &&
          guessBucketFromPeticion(p) === activeBucket
      ),
    [peticiones, activeBucket]
  );

  // Reset página al cambiar de bucket
  useEffect(() => { setPage(0); }, [activeBucket]);

  const totalPages = Math.max(1, Math.ceil(activePeticiones.length / PAGE_SIZE));
  const currentSlots = useMemo(() => {
    const start = page * PAGE_SIZE;
    const slots = activePeticiones.slice(start, start + PAGE_SIZE);
    // Rellenar con nulls hasta 8 para mantener la parrilla
    while (slots.length < PAGE_SIZE) slots.push(null);
    return slots;
  }, [activePeticiones, page]);

  // Si cambia la lista y la página queda "fuera de rango", volver a 0.
  useEffect(() => {
    if (page > 0 && page >= totalPages) setPage(0);
  }, [page, totalPages]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
        } catch (e) { /* silencioso */ }
      }
      // Revocar blob URLs pendientes → libera memoria en iOS
      blobUrlsToRevokeRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (e) { /* silencioso */ }
      });
      blobUrlsToRevokeRef.current = [];
    };
  }, []);

  // --- Playback engine ------------------------------------------------------

  const stopYoutube = useCallback(() => {
    const p = ytPlayerRef.current;
    if (p && p.stopVideo) {
      try { p.stopVideo(); } catch (e) { /* silencioso */ }
    } else if (youtubeIframeRef.current) {
      // Fallback si el player nativo no está listo
      youtubeIframeRef.current.src = 'about:blank';
    }
    if (ytProgressIntervalRef.current) {
      clearInterval(ytProgressIntervalRef.current);
      ytProgressIntervalRef.current = null;
    }
  }, []);

  const fadeOutAndStop = useCallback(
    (onDone) => {
      if (currentPlayerRef.current === 'mp3' && audioRef.current) {
        const audio = audioRef.current;
        const startVol = audio.volume;
        const steps = 16;
        const stepMs = FADE_OUT_MS / steps;
        let i = 0;
        if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = setInterval(() => {
          i += 1;
          const nextVol = Math.max(0, startVol * (1 - i / steps));
          try { audio.volume = nextVol; } catch (e) { /* iOS bloquea, seguimos */ }
          if (i >= steps) {
            clearInterval(fadeIntervalRef.current);
            fadeIntervalRef.current = null;
            try {
              audio.pause();
              audio.currentTime = 0;
              audio.volume = 1;
            } catch (e) { /* silencioso */ }
            onDone && onDone();
          }
        }, stepMs);
      } else if (currentPlayerRef.current === 'youtube') {
        // YouTube iframe no permite fade programático → corte limpio inmediato.
        stopYoutube();
        onDone && onDone();
      } else {
        onDone && onDone();
      }
    },
    [stopYoutube]
  );

  // Guarda blob URLs creados dinámicamente para revocarlos en cleanup.
  const blobUrlsToRevokeRef = useRef([]);

  const playMp3 = useCallback(
    async (peticion) => {
      const audio = audioRef.current;
      if (!audio) return;

      // 1) Intento cache IndexedDB (blob) — patrón que usa SortablePeticionItem
      //    y que es MUY superior en iOS: la reproducción arranca instantánea
      //    porque no hay fetch en curso. Sin blob, iOS rechaza `audio.play()`
      //    en muchos casos con AbortError/NotAllowedError (bug reportado:
      //    "el badge LIVE aparece un instante y desaparece").
      let src = null;
      try {
        const cached = await getAudioFromCache(peticion.audio_file_id);
        if (cached && cached.blob) {
          src = URL.createObjectURL(cached.blob);
          blobUrlsToRevokeRef.current.push(src);
        }
      } catch (cacheErr) {
        // Silencioso — caeremos al backend URL.
      }
      // 2) Fallback a URL directa del backend (con token).
      if (!src) {
        src = `${BACKEND_URL}/api/audio/${peticion.audio_file_id}?token=${token}`;
      }

      audio.src = src;
      audio.volume = 1;

      // Auto-CUE: si la petición tiene cues, arrancar desde el primero.
      const firstCue = peticion.cues && peticion.cues[0];
      pendingCueSeekRef.current =
        firstCue && typeof firstCue.time === 'number' && firstCue.time > 0
          ? firstCue.time
          : null;

      // iOS: forzar load() antes de play() reinicializa el contexto del audio
      // y aumenta drásticamente la tasa de éxito del primer play().
      try {
        audio.load();
      } catch (e) { /* silencioso */ }

      // Play con retry: si iOS rechaza (AbortError/NotAllowedError por src
      // aún cargando), reintenta hasta 2 veces con delays cortos preservando
      // el "user gesture" (dentro del mismo microtask tras la primera vez).
      const attemptPlay = (attempt = 0) => {
        const p = audio.play();
        if (!p || typeof p.catch !== 'function') return;
        p.catch((err) => {
          const retriable =
            err && (err.name === 'AbortError' || err.name === 'NotAllowedError');
          if (retriable && attempt < 2) {
            // Delay incremental: 60ms, luego 180ms
            const delay = 60 + attempt * 120;
            setTimeout(() => attemptPlay(attempt + 1), delay);
            return;
          }
          console.warn('[LiveDashboard] audio.play() rejected:', err && err.name, err && err.message);
          toast.warning('No se pudo iniciar la reproducción. Pulsa de nuevo.', {
            id: `pad-mp3-play-err`,
            duration: 2500,
          });
          setLoadingId(null);
          setActiveId(null);
          setProgress(0);
          currentPlayerRef.current = null;
        });
      };
      attemptPlay();
      currentPlayerRef.current = 'mp3';
    },
    [token]
  );

  const playYoutube = useCallback((peticion) => {
    const ytId = extractYoutubeId(peticion.youtube_url);
    if (!ytId) return;
    const firstCue = peticion.cues && peticion.cues[0];
    const startSeconds =
      firstCue && typeof firstCue.time === 'number' && firstCue.time > 0
        ? Math.floor(firstCue.time)
        : 0;

    const startVideo = () => {
      const player = ytPlayerRef.current;
      if (player && player.loadVideoById) {
        player.loadVideoById({ videoId: ytId, startSeconds });
      }
    };

    // Si el player ya está listo → carga directamente.
    // Si aún no → guarda como pending y se arranca en `onReady`.
    if (ytPlayerRef.current && ytPlayerRef.current.loadVideoById) {
      startVideo();
    } else {
      pendingYouTubeRef.current = { videoId: ytId, startSeconds };
      // Fallback duro: si en 3s la API sigue sin cargar, embed directo (sin progress)
      setTimeout(() => {
        if (
          pendingYouTubeRef.current &&
          pendingYouTubeRef.current.videoId === ytId &&
          youtubeIframeRef.current
        ) {
          youtubeIframeRef.current.src =
            `https://www.youtube.com/embed/${ytId}?autoplay=1&playsinline=1&modestbranding=1&rel=0&enablejsapi=1${startSeconds ? `&start=${startSeconds}` : ''}`;
          pendingYouTubeRef.current = null;
        }
      }, 3000);
    }

    currentPlayerRef.current = 'youtube';

    // Poll de progreso cada 300ms
    if (ytProgressIntervalRef.current) clearInterval(ytProgressIntervalRef.current);
    ytProgressIntervalRef.current = setInterval(() => {
      const player = ytPlayerRef.current;
      if (!player || !player.getCurrentTime || !player.getDuration) return;
      const cur = player.getCurrentTime();
      const dur = player.getDuration();
      if (dur > 0 && isFinite(dur)) {
        const ratio = Math.min(1, Math.max(0, cur / dur));
        setProgress(ratio);
        if (ratio >= PLAYED_THRESHOLD) markPlayed(peticion.id);
      }
    }, 300);
  }, [markPlayed]);

  const handleTap = useCallback(
    (peticion) => {
      if (!peticion) return;
      haptic(15);
      const kind = getSourceKind(peticion);

      // Sólo-Spotify: deep-link externo, no interfiere con el reproductor interno.
      // Feb-2026: resolveSpotifyUrl garantiza siempre un enlace válido (búsqueda si no hay ID).
      if (kind === 'spotify') {
        const resolved = resolveSpotifyUrl(peticion);
        if (resolved) openExternalLink(resolved.url);
        return;
      }

      // Si ya está sonando el mismo → toggle stop.
      if (activeId === peticion.id) {
        fadeOutAndStop(() => {
          setActiveId(null);
          setProgress(0);
          currentPlayerRef.current = null;
        });
        return;
      }

      // Guard anti-doble-tap
      if (switchingRef.current) return;
      switchingRef.current = true;

      setLoadingId(peticion.id);

      const startNew = () => {
        setLoadingId(null);
        setActiveId(peticion.id);
        setProgress(0);
        if (kind === 'mp3') playMp3(peticion);
        else if (kind === 'youtube') playYoutube(peticion);
        switchingRef.current = false;
      };

      if (currentPlayerRef.current) {
        fadeOutAndStop(startNew);
      } else {
        startNew();
      }
    },
    [activeId, fadeOutAndStop, playMp3, playYoutube]
  );

  // Cuando el <audio> termina naturalmente, limpia el estado.
  const onAudioEnded = () => {
    setActiveId(null);
    setProgress(0);
    currentPlayerRef.current = null;
  };

  // Al cargar los metadatos, aplica el seek al CUE si estaba pendiente.
  const onAudioLoadedMetadata = () => {
    const audio = audioRef.current;
    const cueTime = pendingCueSeekRef.current;
    if (!audio || cueTime == null) return;
    try {
      const target = Math.max(0, Math.min(cueTime, audio.duration || cueTime));
      audio.currentTime = target;
    } catch (e) {
      // silencioso — algún navegador puede rechazar seek antes de tiempo
    }
    pendingCueSeekRef.current = null;
  };

  // Actualiza la barra de progreso del botón activo + marca reproducido al 30%.
  const onAudioTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || !isFinite(audio.duration)) return;
    const ratio = audio.currentTime / audio.duration;
    setProgress(ratio);
    if (ratio >= PLAYED_THRESHOLD && activeId) markPlayed(activeId);
  };

  // --- UI helpers -----------------------------------------------------------

  const goPrev = () => {
    haptic(8);
    setPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    haptic(8);
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  // Swipe touch handlers para paginación
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 60) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="bg-black text-white -mx-4 sm:-mx-6 -my-4 min-h-[calc(100vh-14rem)] flex flex-col"
      style={{ padding: 0 }}
      data-testid="live-dashboard"
    >
      {/* Cabecera compacta: paginación */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="text-xs uppercase tracking-widest text-white/60">
          Live Pad · {activePeticiones.length} pistas
        </span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={goPrev}
              disabled={page === 0}
              className="h-9 w-9 flex items-center justify-center rounded-md bg-white/10 disabled:opacity-30"
              data-testid="live-pad-prev"
              aria-label="Página anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-xs text-white/70 tabular-nums w-10 text-center">
              {page + 1}/{totalPages}
            </span>
            <button
              onClick={goNext}
              disabled={page >= totalPages - 1}
              className="h-9 w-9 flex items-center justify-center rounded-md bg-white/10 disabled:opacity-30"
              data-testid="live-pad-next"
              aria-label="Página siguiente"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Grid pad — 4x2 en landscape/tablet, 2x4 en portrait/móvil */}
      <div
        className="flex-1 grid gap-1 p-1 grid-cols-2 grid-rows-4 landscape:grid-cols-4 landscape:grid-rows-2 sm:grid-cols-4 sm:grid-rows-2"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="live-pad-grid"
      >
        {currentSlots.map((peticion, i) => {
          const accent = SLOT_ACCENTS[i % SLOT_ACCENTS.length];
          const isActive = peticion && activeId === peticion.id;
          const isLoading = peticion && loadingId === peticion.id;
          const kind = peticion ? getSourceKind(peticion) : 'none';
          // "Ya reproducida (visual)": alcanzó ≥30% en algún momento durante
          // este evento. NO oculta la pista, sólo la atenúa. El estado se
          // desactiva SOLO cuando el botón está activo actualmente sonando.
          const isPlayed = peticion && !isActive && playedIds.has(peticion.id);
          // Etiqueta a mostrar: `momento` específico si lo hay; si es un
          // placeholder genérico de bucket (ej. "Baile General"), se prioriza
          // la `nota` del DJ (ej. "Hora loca") por ser más concreta.
          const displayLabel = peticion ? getDisplayLabel(peticion) : '';

          if (!peticion) {
            return (
              <div
                key={`empty-${i}`}
                className="rounded-md flex items-center justify-center text-white/20 text-2xl font-thin border border-white/5"
                data-testid={`live-pad-slot-empty-${i}`}
              >
                —
              </div>
            );
          }

          return (
            <div
              key={peticion.id}
              role="button"
              tabIndex={0}
              onClick={() => handleTap(peticion)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTap(peticion);
                }
              }}
              className="relative rounded-md text-left overflow-hidden transition-transform active:scale-95 focus:outline-none cursor-pointer"
              style={{
                border: `2px solid ${isPlayed ? 'rgba(255,255,255,0.18)' : accent.border}`,
                boxShadow: isActive
                  ? `0 0 24px 4px ${accent.glow}, inset 0 0 24px ${accent.glow}`
                  : `0 0 0 rgba(0,0,0,0)`,
                background: isActive
                  ? `linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))`
                  : 'rgba(255,255,255,0.02)',
                opacity: isPlayed ? 0.55 : 1,
                filter: isPlayed ? 'grayscale(0.8)' : 'none',
              }}
              data-testid={`live-pad-slot-${peticion.id}`}
              data-played={isPlayed ? '1' : '0'}
            >
              {/* Origen (esquina sup-izq) */}
              <div
                className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: 'rgba(0,0,0,0.55)', color: accent.border }}
              >
                <SourceIcon kind={kind} />
                <span className="uppercase tracking-wider">{kind}</span>
              </div>

              {/* Estado playing (esquina sup-der) */}
              {isActive && (
                <div
                  className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: accent.border, color: '#000' }}
                >
                  <span className="animate-pulse">●</span> LIVE
                </div>
              )}
              {isLoading && !isActive && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white">
                  ...
                </div>
              )}
              {/* Checkmark "ya reproducida" (visual, no oculta la pista). */}
              {isPlayed && (
                <div
                  className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/25 text-white"
                  data-testid={`live-pad-played-${peticion.id}`}
                  title="Ya reproducida (≥30%)"
                >
                  ✓
                </div>
              )}

              {/* Título grande centrado */}
              <div className="h-full flex flex-col items-center justify-center px-3 text-center">
                <p
                  className="font-extrabold leading-tight text-white line-clamp-3"
                  style={{ fontSize: 'clamp(16px, 3.5vw, 28px)' }}
                >
                  {peticion.title || peticion.titulo || 'Sin título'}
                </p>
              </div>

              {/* Barra de progreso (mientras suena). Funciona para MP3
                  (evento onTimeUpdate) y para YouTube (IFrame API polling). */}
              {isActive && (kind === 'mp3' || kind === 'youtube') && (
                <div
                  className="absolute left-0 right-0 h-1"
                  style={{ bottom: displayLabel ? 40 : 0, background: 'rgba(255,255,255,0.12)' }}
                  data-testid={`live-pad-progress-${peticion.id}`}
                >
                  <div
                    className="h-full transition-[width] duration-150 ease-linear"
                    style={{
                      width: `${Math.round(progress * 100)}%`,
                      background: accent.border,
                      boxShadow: `0 0 8px ${accent.glow}`,
                    }}                  />
                </div>
              )}
              {/* Marca visual "arranca desde CUE" si aplica */}
              {peticion.cues && peticion.cues[0] && (
                <div
                  className="absolute top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest"
                  style={{ background: 'rgba(0,0,0,0.55)', color: accent.border }}
                  title={`Arranca en CUE ${peticion.cues[0].label || 'A'}`}
                >
                  ▸ CUE {peticion.cues[0].label || 'A'}
                </div>
              )}

              {/* Etiqueta inferior: prioriza `momento` específico (Entrada Banquete,
                  Corte Tarta, etc.), pero si `momento` es un placeholder
                  genérico de bucket (ej. "Baile General"), usa la `nota` del
                  DJ cuando es más concreta (ej. "Hora loca"). Ver `getDisplayLabel`. */}
              {displayLabel && (
                <div
                  className="absolute bottom-2 left-2 right-2 truncate rounded px-2 py-1 text-sm sm:text-base font-semibold uppercase tracking-wide text-center"
                  style={{ background: 'rgba(0,0,0,0.65)', color: accent.border }}
                  data-testid={`live-pad-label-${peticion.id}`}
                >
                  {displayLabel}
                </div>
              )}

              {/* Botón Spotify dedicado (feb-2026) — visible SIEMPRE que haya
                  datos suficientes (URL/URI/ID o título+artista para búsqueda).
                  stopPropagation para no disparar el tap principal del card. */}
              {(() => {
                const sp = resolveSpotifyUrl(peticion);
                if (!sp) return null;
                // Si hay etiqueta inferior visible (momento/nota), subimos el botón para no taparla.
                const bottomOffset = displayLabel ? 52 : 8;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      haptic(10);
                      openExternalLink(sp.url);
                    }}
                    style={{ bottom: `${bottomOffset}px`, right: '8px' }}
                    className={`absolute flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold text-white shadow-lg ${sp.isSearch ? 'bg-emerald-800/90 hover:bg-emerald-700' : 'bg-green-600/90 hover:bg-green-500'}`}
                    title={sp.isSearch ? 'Buscar en Spotify' : 'Abrir en Spotify'}
                    data-testid={`live-pad-spotify-btn-${peticion.id}`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span className="uppercase tracking-wider">
                      {sp.isSearch ? 'BUSCAR' : 'SPOTIFY'}
                    </span>
                  </button>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Reproductor MP3 oculto (control programático) */}
      <audio
        ref={audioRef}
        preload="none"
        onEnded={onAudioEnded}
        onLoadedMetadata={onAudioLoadedMetadata}
        onTimeUpdate={onAudioTimeUpdate}
        style={{ display: 'none' }}
        data-testid="live-pad-audio"
      />

      {/* Reproductor YouTube oculto (0x0, sólo audio en background).
          La IFrame API convierte este <iframe> en instancia YT.Player. */}
      <iframe
        ref={youtubeIframeRef}
        title="YouTube player"
        src="https://www.youtube.com/embed/?enablejsapi=1"
        allow="autoplay; encrypted-media"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          border: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
        data-testid="live-pad-youtube"
      />

      {/* Botón flotante para cambiar de momento activo sin salir del pad */}
      {typeof onActiveBucketChange === 'function' && (
        <LiveMomentoSwitcher
          peticiones={peticiones}
          activeBucket={activeBucket}
          onChange={onActiveBucketChange}
          overrides={momentoOverrides}
        />
      )}
    </div>
  );
};

export default LiveDashboard;
